'use client';

import './archiveCalendar.css';

import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { useUserProvider } from '@wt/game/providers/user/UserProvider';
import { ArchiveSidebarAd } from '@wt/shared/components/ads/ArchiveSidebar';
import { BackButton } from '@wt/shared/components/BackButton';
import { refreshArchiveSidebarAd } from '@wt/shared/hooks/useAds/useAds';
import useAnimatedRouter from '@wt/shared/hooks/useAnimatedRouter';
import { getFirstGameDate, getSiteMode } from '@wt/utilities/siteMode';
import dayjs from 'dayjs';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import Calendar from 'react-calendar';

import PlayAgainModal from '../../../game/modals/PlayAgainModal';
import {
  archiveCalendarBackgroundColor,
  clearArchiveGame,
  EArchiveCalendarState,
  formatDate,
  hasGameBeenCompleted,
  loadArchiveCompleted,
  loadDailyCompleted,
} from '../utils';

export default function ArchiveCalendar() {
  const { animatedRoute } = useAnimatedRouter();
  const { loggedIn } = useUserProvider();
  const [archiveGamesCompleted, setArchiveGamesCompleted] = useState<string[]>(
    []
  );
  const [dailyGamesCompleted, setDailyGamesCompleted] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    const archiveCompleted = loadArchiveCompleted();
    setArchiveGamesCompleted(archiveCompleted);

    const dailyCompleted = loadDailyCompleted();
    setDailyGamesCompleted(dailyCompleted);

    refreshArchiveSidebarAd();
  }, []);

  const handleClickDay = (date: Date) => {
    if (
      dayjs(date).isAfter(
        dayjs(getFirstGameDate()).subtract(1, 'day'),
        'day'
      ) &&
      dayjs(date).isBefore(new Date(), 'day')
    ) {
      setSelectedDate(formatDate(date));
    }
  };

  const navToArchiveGame = (
    date: string,
    clearGuesses: boolean,
    viewResults: boolean
  ): void => {
    if (clearGuesses) {
      clearArchiveGame(date, loggedIn);
    }
    animatedRoute(`/archive/game/${date}${viewResults ? '/summary' : ''}`);
    return;
  };

  const navToRandomGame = (): void => {
    const dates: string[] = [formatDate(dayjs(getFirstGameDate()).toDate())];
    const yesterday = dayjs().subtract(1, 'day');
    while (dayjs(dates[dates.length - 1]).isBefore(yesterday, 'day')) {
      dates.push(
        formatDate(
          dayjs(dates[dates.length - 1])
            .add(1, 'day')
            .toDate()
        )
      );
    }

    const unplayedDates = dates.filter(
      (d) => !archiveGamesCompleted.includes(d)
    );

    if (unplayedDates.length) {
      const randomIndex = Math.floor(Math.random() * unplayedDates.length);
      setSelectedDate(unplayedDates[randomIndex]);
    } else {
      const randomIndex = Math.floor(Math.random() * dates.length);
      setSelectedDate(dates[randomIndex]);
    }
  };

  useEffect(() => {
    if (
      !isNil(selectedDate) &&
      hasGameBeenCompleted(archiveGamesCompleted, selectedDate)
    ) {
      setShowModal(true);
    } else if (!isNil(selectedDate)) {
      navToArchiveGame(selectedDate, false, false);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!showModal) {
      setSelectedDate(null);
    }
  }, [showModal]);

  return (
    <div className="flex h-full flex-1 grow flex-col items-center gap-2 px-4 py-2 md:gap-5 md:px-8 md:py-5">
      <ArchiveSidebarAd />
      <div className="relative flex w-full max-w-[840px] items-center justify-center">
        <BackButton />
        <div className="w-full items-center justify-center text-center text-xl font-medium lg:text-4xl">
          Archive mode
        </div>
      </div>
      <div className="flex w-full max-w-[840px] flex-col items-center justify-center gap-x-5 gap-y-2 md:flex-row">
        <div className="flex flex-row gap-2">
          <div className="relative flex size-4 items-center justify-center rounded-full border border-accent bg-primary text-sm">
            <StarRoundedIcon
              fontSize="inherit"
              htmlColor="white"
              className="translate-x-[0.1px]"
            />
          </div>
          <p className="text-xs">Completed in Daily mode</p>
        </div>
        <div className="flex flex-row gap-2">
          <div className="relative flex size-4 items-center justify-center rounded-full border border-accent bg-primary text-sm">
            <CheckRoundedIcon fontSize="inherit" htmlColor="white" />
          </div>
          <p className="text-xs">Completed in Archive mode</p>
        </div>
      </div>
      <Calendar
        minDate={dayjs(getFirstGameDate()).toDate()}
        minDetail={'year'}
        formatDay={() => ''}
        onClickDay={handleClickDay}
        tileContent={({ date, view }) =>
          view === 'month' ? (
            <CalendarTileContent
              date={date}
              isCompleted={hasGameBeenCompleted(archiveGamesCompleted, date)}
              isDailyGameCompleted={hasGameBeenCompleted(
                dailyGamesCompleted,
                date
              )}
            />
          ) : null
        }
        tileClassName={({ date, view }) =>
          view === 'month' &&
          dayjs(date).isAfter(dayjs(new Date()).subtract(1, 'day'), 'day')
            ? 'upcoming'
            : null
        }
      />
      <div className="flex flex-col items-center justify-center gap-2 md:flex-row">
        <span className="text-center text-sm md:text-lg">
          Choose the date of the game you want to replay or
        </span>
        <button
          type="button"
          onClick={navToRandomGame}
          className="btn btn-primary h-[33px] min-h-[33px] shrink text-sm font-normal text-white movies:text-primary-content md:h-[40px] md:min-h-[40px] md:text-base"
        >
          <ShuffleIcon className="pr-1" /> Play a random game
        </button>
      </div>
      {showModal && !isNil(selectedDate) && (
        <PlayAgainModal
          closeModal={() => setShowModal(false)}
          handleClickSeeResults={() =>
            navToArchiveGame(selectedDate, false, true)
          }
          handleClickPlayAgain={() =>
            navToArchiveGame(selectedDate, true, false)
          }
        />
      )}
    </div>
  );
}

const CalendarTileContent = ({
  date,
  isCompleted,
  isDailyGameCompleted,
}: {
  date: Date;
  isCompleted: boolean;
  isDailyGameCompleted: boolean;
}) => {
  let tileState = EArchiveCalendarState.noPuzzle;

  if (dayjs(date).isAfter(dayjs(new Date()).subtract(1, 'day'), 'day')) {
    tileState = EArchiveCalendarState.upcoming;
  } else if (
    dayjs(date).isAfter(dayjs(getFirstGameDate()).subtract(1, 'day'), 'day')
  ) {
    tileState = EArchiveCalendarState.hasPuzzle;
  }

  return (
    <div className="relative flex flex-col items-center justify-center">
      {isCompleted && (
        <div className="absolute -right-1.5 -top-2 z-10 flex size-4 items-center justify-center rounded-full border border-accent bg-primary text-sm font-bold md:-right-0.5 md:-top-1 md:size-5 md:text-base">
          <CheckRoundedIcon fontSize="inherit" htmlColor="white" />
        </div>
      )}
      {isDailyGameCompleted && (
        <div className="absolute -left-1.5 -top-2 z-10 flex size-4 items-center justify-center rounded-full border border-accent bg-primary text-sm font-bold md:-left-0.5 md:-top-1 md:size-5 md:text-base">
          <StarRoundedIcon
            fontSize="inherit"
            htmlColor="white"
            className="translate-x-[-0.1px]"
          />
        </div>
      )}
      <div
        className={`flex h-6 w-8 items-center justify-center md:h-7 md:w-10 ${archiveCalendarBackgroundColor(tileState)} rounded-md text-white movies:text-primary-content`}
      >
        {date.getDate()}
      </div>
      <div className="hidden pt-2 text-xs text-gray-500 md:flex">
        {dayjs(date).format('DD/MM/YYYY')}
      </div>
    </div>
  );
};
